import { useState, type FormEvent } from "react";
import { useSeo } from "@/hooks/useSeo";

const submitAddr = "https://readdy.ai/api/form/db6fgpvqhq9pk5mectgg";

const topicOptions = ["影片资讯咨询", "内容合作", "版权与授权", "问题反馈", "其他"];

const contactInfo = [
  { icon: "ri-mail-line", label: "联系邮箱", value: "contact@peach3d-movie.com" },
  { icon: "ri-phone-line", label: "客服电话", value: "400-620-2026" },
  { icon: "ri-time-line", label: "服务时间", value: "工作日 9:00 - 18:00" },
  { icon: "ri-map-pin-line", label: "办公地址", value: "蜜桃海岸小镇 · 影视文化园区 B 座" },
];

const socials = [
  { icon: "ri-weibo-fill", label: "微博" },
  { icon: "ri-wechat-fill", label: "微信" },
  { icon: "ri-youtube-fill", label: "视频频道" },
  { icon: "ri-instagram-fill", label: "图片分享" },
];

function extractMessage(parsed: unknown, fallback: string): string {
  if (parsed && typeof parsed === "object") {
    const obj = parsed as { message?: unknown; meta?: { message?: unknown; detail?: unknown } };
    const metaMsg = obj.meta?.message;
    const metaDetail = obj.meta?.detail;
    if (typeof metaMsg === "string" && metaMsg) return metaMsg;
    if (typeof metaDetail === "string" && metaDetail) return metaDetail;
    if (typeof obj.message === "string" && obj.message) return obj.message;
  }
  return fallback;
}

function extractCode(parsed: unknown): string {
  if (parsed && typeof parsed === "object") {
    const code = (parsed as { code?: unknown }).code;
    if (typeof code === "string") return code;
  }
  return "";
}

export default function ContactPage() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [fieldError, setFieldError] = useState("");

  useSeo({
    title: "联系我们 - 蜜桃成熟时3D",
    description:
      "如需咨询蜜桃成熟时3D影片资讯、内容合作或版权相关事宜，可通过在线留言表单、邮箱或客服电话与我们取得联系。",
    keywords: "联系我们,蜜桃成熟时3D,留言反馈,联系方式",
    path: "/contact",
  });

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      setFieldError("");
      form.reset();
      return;
    }

    const name = String(formData.get("name") ?? "").trim();
    const email = String(formData.get("email") ?? "").trim();
    const message = String(formData.get("message") ?? "").trim();

    if (!name || !email || !message) {
      setStatus("error");
      setFieldError("请完整填写姓名、邮箱与留言内容。");
      setFormError("");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("error");
      setFieldError("请输入有效的邮箱地址。");
      setFormError("");
      return;
    }
    if (message.length > 500) {
      setStatus("error");
      setFieldError("留言内容不能超过 500 个字符。");
      setFormError("");
      return;
    }

    formData.delete("website_alt");
    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, String(value));
    });

    setStatus("submitting");
    setFieldError("");
    setFormError("");

    try {
      const response = await fetch(submitAddr, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: unknown = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const code = extractCode(parsed);
      const serverMsg = extractMessage(parsed, responseText);
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg.trim()
            ? serverMsg
            : "提交未成功，请稍后重试或通过邮箱与我们联系。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络连接异常，请检查网络后重试。");
    }
  }

  return (
    <>
      <section className="bg-background-100">
        <div className="mx-auto max-w-[1200px] px-4 py-12 md:px-6 md:py-16">
          <span className="mb-2 inline-flex items-center gap-2 text-xs font-medium tracking-widest text-accent-600">
            <span className="h-1.5 w-1.5 rounded-full bg-accent-500"></span>
            CONTACT
          </span>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-4xl">联系我们</h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            如果你对「蜜桃成熟时3D」影片资讯、内容合作或版权事宜有任何疑问，欢迎通过以下方式与我们取得联系。我们会尽快回复你的留言。
          </p>
        </div>
      </section>

      <section className="bg-background-50 py-12 md:py-16">
        <div className="mx-auto max-w-[1200px] px-4 md:px-6">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-5 lg:gap-10">
            <div className="lg:col-span-3">
              <div className="rounded-lg border border-background-200 bg-background-100/50 p-5 md:p-8">
                <h2 className="mb-1 font-heading text-xl font-bold text-foreground-950">在线留言</h2>
                <p className="mb-6 text-sm text-foreground-500">带 * 的为必填项，留言内容请控制在 500 字以内。</p>

                <form data-readdy-form onSubmit={handleSubmit} noValidate className="space-y-5">
                  <input
                    type="text"
                    name="website_alt"
                    className="field-honeypot"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="name" className="mb-2 block text-sm font-medium text-foreground-800">
                        姓名 <span className="text-accent-600">*</span>
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        placeholder="请输入你的称呼"
                        className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-1 focus:ring-primary-300"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="mb-2 block text-sm font-medium text-foreground-800">
                        邮箱 <span className="text-accent-600">*</span>
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="用于接收回复"
                        className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-1 focus:ring-primary-300"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="topic" className="mb-2 block text-sm font-medium text-foreground-800">
                      咨询类型
                    </label>
                    <select
                      id="topic"
                      name="topic"
                      defaultValue={topicOptions[0]}
                      className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-1 focus:ring-primary-300"
                    >
                      {topicOptions.map((topic) => (
                        <option key={topic} value={topic}>
                          {topic}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <label htmlFor="message" className="block text-sm font-medium text-foreground-800">
                        留言内容 <span className="text-accent-600">*</span>
                      </label>
                      <span className="text-xs text-foreground-400">最多 500 字</span>
                    </div>
                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      maxLength={500}
                      placeholder="请简要描述你的问题或建议"
                      className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-1 focus:ring-primary-300"
                    ></textarea>
                  </div>

                  {status === "success" && (
                    <div className="flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800">
                      <i className="ri-checkbox-circle-line mt-0.5 w-4 h-4 flex items-center justify-center"></i>
                      <span>留言已提交，感谢你的反馈，我们会尽快与你联系。</span>
                    </div>
                  )}
                  {status === "error" && (formError || fieldError) && (
                    <div className="flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-3 text-sm text-accent-800">
                      <i className="ri-error-warning-line mt-0.5 w-4 h-4 flex items-center justify-center"></i>
                      <span>{fieldError || formError}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                  >
                    {status === "submitting" ? (
                      <>
                        <i className="ri-loader-4-line w-4 h-4 flex items-center justify-center animate-spin"></i>
                        提交中…
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line w-4 h-4 flex items-center justify-center"></i>
                        提交留言
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>

            <aside className="lg:col-span-2">
              <div className="rounded-lg border border-background-200 bg-background-100/50 p-5 md:p-6">
                <h2 className="mb-5 font-heading text-lg font-bold text-foreground-950">联系方式</h2>
                <ul className="space-y-4">
                  {contactInfo.map((info) => (
                    <li key={info.label} className="flex items-start gap-3">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background-50 text-primary-600">
                        <i className={`${info.icon} text-lg`}></i>
                      </span>
                      <span className="pt-0.5">
                        <span className="block text-xs text-foreground-400">{info.label}</span>
                        <span className="block text-sm font-medium text-foreground-800">{info.value}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-6 rounded-lg bg-primary-50 p-5 md:p-6">
                <h2 className="mb-3 font-heading text-lg font-bold text-foreground-950">关注我们</h2>
                <p className="mb-4 text-sm leading-relaxed text-foreground-600">
                  在社交平台关注我们，第一时间获取影片动态与活动信息。
                </p>
                <div className="flex flex-wrap gap-2">
                  {socials.map((s) => (
                    <span
                      key={s.label}
                      title={s.label}
                      className="flex h-10 w-10 items-center justify-center rounded-md bg-background-50 text-foreground-600 transition-colors hover:bg-accent-500 hover:text-background-50"
                    >
                      <i className={`${s.icon} text-lg`}></i>
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-100/50 p-5 text-xs leading-relaxed text-foreground-500">
                温馨提示：本站为演示站点，联系方式与地址均为虚构内容，请勿据此进行实际联系。
              </div>
            </aside>
          </div>
        </div>
      </section>
    </>
  );
}